// Single source of truth for all JSON-LD on this site.
//
// Every route emits EXACTLY ONE <script type="application/ld+json"> through
// the route's own head() -> scripts array, so the markup is present in the
// server-rendered HTML (view-source), never injected after hydration.
//
// Hard rules (do not break in future work):
//   - Never add Review, AggregateRating, Product or SearchAction markup.
//   - Every URL in JSON-LD is absolute.
//   - Output must be valid JSON: no trailing commas, no comments.

export const SITE_URL = "https://arifkhanglobal.com";

export function absUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

// Permanent entity IDs. Arif Khan (Person) lives on this personal site;
// Altima Global (Organization) is a separate entity on its own domain.
export const PERSON_ID = `${SITE_URL}/#person`;
export const ALTIMA_URL = "https://www.altimaglobal.com/";
export const ORG_ID = "https://www.altimaglobal.com/#organization";
export const PRACTICE_ID = `${SITE_URL}/#practice`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const PORTRAIT_ID = `${SITE_URL}/#primary-portrait`;

// Official primary portrait — stable, crawlable public URL (never hashed).
export const PRIMARY_PORTRAIT_PATH =
  "/images/arif-khan/arif-khan-altima-global-building-technology-strategist.jpg";
export const PRIMARY_PORTRAIT_URL = `${SITE_URL}${PRIMARY_PORTRAIT_PATH}`;
export const PRIMARY_PORTRAIT_WIDTH = 1886;
export const PRIMARY_PORTRAIT_HEIGHT = 1756;
export const PRIMARY_PORTRAIT_ALT =
  "Arif Khan, Security Infrastructure and Building Technology Strategist and BU Head at Altima Global, seated in a professional studio setting.";
export const PRIMARY_PORTRAIT_CAPTION =
  "Arif Khan, Security Infrastructure & Building Technology Strategist and BU Head at Altima Global, New Delhi.";

const ADDRESS = {
  "@type": "PostalAddress",
  streetAddress:
    "4th Floor, Statesman House Building, Barakhamba Road, Connaught Place",
  addressLocality: "New Delhi",
  addressRegion: "Delhi",
  postalCode: "110001",
  addressCountry: "IN",
};

const SAME_AS = [
  "https://www.linkedin.com/in/arifkhan-altimaglobal",
  "https://www.youtube.com/@arifkhanglobal",
  "https://www.instagram.com/arifkhan_altima",
  "https://www.twitter.com/arifkhan_altima",
];

export const PORTRAIT_NODE = {
  "@type": "ImageObject",
  "@id": PORTRAIT_ID,
  url: PRIMARY_PORTRAIT_URL,
  contentUrl: PRIMARY_PORTRAIT_URL,
  width: PRIMARY_PORTRAIT_WIDTH,
  height: PRIMARY_PORTRAIT_HEIGHT,
  caption: PRIMARY_PORTRAIT_CAPTION,
  representativeOfPage: true,
};

export const PERSON_NODE = {
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Arif Khan",
  url: `${SITE_URL}/`,
  image: { "@id": PORTRAIT_ID },
  jobTitle: "BU Head, Security & Automation",
  hasOccupation: {
    "@type": "Occupation",
    name: "Security Infrastructure & Building Technology Strategist",
  },
  description:
    "Security Infrastructure and Building Technology Strategist with 23+ years of experience across electronic security, perimeter protection, smart automation, luxury home cinema and professional audio-video systems.",
  worksFor: { "@id": ORG_ID },
  telephone: "+91-9818928760",
  email: "mailto:arif@arifkhanglobal.com",
  workLocation: { "@type": "Place", address: ADDRESS },
  sameAs: SAME_AS,
  knowsAbout: [
    "Electronic Security",
    "Security Infrastructure",
    "Perimeter Protection",
    "Smart Home Automation",
    "Luxury Home Cinema",
    "Professional Audio Video",
    "Auditorium AV",
    "Perimeter intrusion detection systems (PIDS)",
    "CCTV surveillance and command centre design",
    "Access control and biometrics",
    "Intelligent building management systems (IBMS)",
    "Enterprise AV",
  ],
};

export const ORGANIZATION_NODE = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Altima Global",
  url: ALTIMA_URL,
  address: ADDRESS,
};

export const PRACTICE_NODE = {
  "@type": "ProfessionalService",
  "@id": PRACTICE_ID,
  name: "Arif Khan — Security & Building Technology Consulting",
  url: `${SITE_URL}/`,
  image: { "@id": PORTRAIT_ID },
  telephone: "+91-9818928760",
  email: "mailto:arif@arifkhanglobal.com",
  founder: { "@id": PERSON_ID },
  address: ADDRESS,
  areaServed: [
    { "@type": "City", name: "New Delhi" },
    { "@type": "City", name: "Delhi" },
    { "@type": "City", name: "Gurugram" },
    { "@type": "City", name: "Noida" },
    { "@type": "City", name: "Faridabad" },
    { "@type": "City", name: "Ghaziabad" },
    { "@type": "City", name: "Meerut" },
    { "@type": "City", name: "Mumbai" },
    { "@type": "City", name: "Lucknow" },
    { "@type": "City", name: "Bengaluru" },
    { "@type": "Country", name: "India" },
  ],
  sameAs: SAME_AS,
};

export const WEBSITE_NODE = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: "Arif Khan",
  alternateName: "Arif Khan — Security & Building Technology Strategist",
  inLanguage: "en-IN",
  publisher: { "@id": PERSON_ID },
  about: { "@id": PERSON_ID },
};

export const SHARED_NODES = [
  WEBSITE_NODE,
  PERSON_NODE,
  PORTRAIT_NODE,
  ORGANIZATION_NODE,
  PRACTICE_NODE,
];

type Node = Record<string, unknown>;

export function buildPageSchema(pageNodes: Node[] = []) {
  return {
    "@context": "https://schema.org",
    "@graph": [...SHARED_NODES, ...pageNodes.filter(Boolean)],
  };
}

/** Ready-to-spread entry for a route's head().scripts array. */
export function schemaScript(pageNodes: Node[] = []) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(buildPageSchema(pageNodes)),
  };
}

export type Crumb = { name: string; url?: string };

/**
 * BreadcrumbList node. "Home" is prepended automatically; pass only the trail
 * below it, ending with the current page. The final item carries name only.
 */
export function breadcrumbNode(path: string, trail: Crumb[]) {
  const items = [{ name: "Home", url: "/" }, ...trail];
  return {
    "@type": "BreadcrumbList",
    "@id": `${absUrl(path)}#breadcrumb`,
    itemListElement: items.map((c, i) => {
      const last = i === items.length - 1;
      return {
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        ...(last || !c.url ? {} : { item: absUrl(c.url) }),
      };
    }),
  };
}

export type PageNodeInput = {
  /** Route path, e.g. "/about". */
  path: string;
  /** Schema type: WebPage, ProfilePage, ContactPage, CollectionPage, ... */
  type: string;
  /** Same string the route's meta title uses. */
  name: string;
  /** Same string the route's meta description uses. */
  description: string;
  /** Breadcrumb trail below Home, ending with the current page. */
  breadcrumb?: Crumb[];
  /** Extra properties merged into the page node (mainEntity, about, ...). */
  extra?: Node;
};

export function pageNode({
  path,
  type,
  name,
  description,
  breadcrumb,
  extra,
}: PageNodeInput): Node[] {
  const url = absUrl(path);
  const crumb = breadcrumb && breadcrumb.length ? breadcrumbNode(path, breadcrumb) : null;
  const node: Node = {
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    isPartOf: { "@id": WEBSITE_ID },
    inLanguage: "en-IN",
    ...(crumb ? { breadcrumb: { "@id": crumb["@id"] as string } } : {}),
    ...(extra ?? {}),
  };
  return crumb ? [node, crumb] : [node];
}

/** Convenience: full script for a simple page. */
export function pageSchemaScript(input: PageNodeInput, extraNodes: Node[] = []) {
  return schemaScript([...pageNode(input), ...extraNodes]);
}
